import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  User, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, setDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { trackUserEvent } from '../utils/analytics';

interface AuthContextType {
  currentUser: User | null;
  login: (email: string, password: string) => Promise<any>;
  signup: (email: string, password: string, age: number) => Promise<any>;
  logout: () => Promise<void>;
  loading: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  async function signup(email: string, password: string, age: number) {
    // console.log('新規登録開始:', { email, age });
    
    try {
      // console.log('Firebase認証でユーザー作成中...');
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      // console.log('ユーザー作成成功:', { uid: user.uid, email: user.email });
      
      // Firestoreにユーザーデータを保存
      try {
        // console.log('Firestoreにユーザーデータ保存中...');
        const userRef = doc(db, 'users', user.uid);
        const userData = {
          uid: user.uid,
          email: user.email,
          age: age,
          isAdmin: false, // デフォルトは一般ユーザー
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        // console.log('保存するユーザーデータ:', userData);
        
        await setDoc(userRef, userData);
        // console.log('Firestore保存成功');
        trackUserEvent.signup();
      } catch (firestoreError: any) {
        console.error('Firestore保存エラー:', firestoreError);
        // Firestore保存に失敗しても認証は成功しているので、ユーザーには成功として返す
        // console.log('Firestore保存に失敗しましたが、認証は成功しています');
      }
      
      return userCredential;
    } catch (error: any) {
      console.error('ユーザー登録エラー詳細:', {
        code: error.code,
        message: error.message,
        email: email
      });
      throw error;
    }
  }

  function login(email: string, password: string) {
    return signInWithEmailAndPassword(auth, email, password).then((result) => {
      trackUserEvent.login();
      return result;
    });
  }

  function logout() {
    return signOut(auth).then(() => {
      trackUserEvent.logout();
    });
  }

  // 管理者権限をチェック
  const checkAdminStatus = async (user: User | null) => {
    if (!user) {
      setIsAdmin(false);
      return;
    }

    try {
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        setIsAdmin(userData.isAdmin === true);
      } else {
        setIsAdmin(false);
      }
    } catch (error) {
      console.error('管理者権限チェックエラー:', error);
      setIsAdmin(false);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      await checkAdminStatus(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    login,
    signup,
    logout,
    loading,
    isAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 